import { useTranslation } from 'react-i18next';
import { XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { TempoMedio, calcularTempoMedioGeral } from '@/types/dashboard';

interface GraficoTempoMedioProps {
  dados: TempoMedio[];
}

export function GraficoTempoMedio({ dados }: GraficoTempoMedioProps) {
  const { t } = useTranslation();
  const mediaGeral = calcularTempoMedioGeral(dados);
  const dadosComValor = dados.filter(d => d.tempoMedio > 0);
  const temDados = dadosComValor.length > 0;

  return (
    <div className="space-y-4">
      {/* Indicador de média */}
      <div className="flex items-center gap-4 p-4 rounded-lg bg-muted/50">
        <div className="text-center">
          <p className="text-3xl font-bold text-primary">{mediaGeral}h</p>
          <p className="text-xs text-muted-foreground">{t('dashboard.charts.generalAverage')}</p>
        </div>
        <div className="flex-1 h-px bg-border" />
        {temDados ? (
          <div className="text-right text-sm text-muted-foreground">
            <p>{t('dashboard.charts.minLabel')}: {Math.min(...dadosComValor.map(d => d.tempoMedio))}h</p>
            <p>{t('dashboard.charts.maxLabel')}: {Math.max(...dadosComValor.map(d => d.tempoMedio))}h</p>
          </div>
        ) : (
          <div className="text-right text-sm text-muted-foreground">
            <p>{t('dashboard.charts.noDataPeriod')}</p>
          </div>
        )}
      </div>

      {/* Gráfico de área */}
      <div className="h-[200px] w-full">
        {temDados ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={dados} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorTempo" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="dia" 
                tick={{ fontSize: 12 }}
                className="text-muted-foreground"
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${value}h`}
                className="text-muted-foreground"
              />
              <Tooltip
                content={({ active, payload, label }) => {
                  if (active && payload && payload.length) {
                    return (
                      <div className="rounded-lg border bg-background p-3 shadow-lg">
                        <p className="font-medium">{label}</p>
                        <p className="text-sm text-muted-foreground">
                          {t('dashboard.charts.averageTimeLabel')} <span className="font-medium text-foreground">{payload[0].value}h</span>
                        </p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Area
                type="monotone"
                dataKey="tempoMedio"
                stroke="hsl(var(--primary))"
                strokeWidth={2}
                fillOpacity={1}
                fill="url(#colorTempo)"
              />
            </AreaChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-muted-foreground">
            {t('dashboard.charts.noCompletedServices')}
          </div>
        )}
      </div>
    </div>
  );
}
